import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { contactUsPageQueryOptions } from "~/app/api/pageData/contactUsPageQuery";
import type { ContactUsPageInitialData } from "~/app/api/pageData/contactUsPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createContactUsPage } from "~/pages/ContactUsPage/ContactUsPage";
import type { ContactUsDisplayProps } from "~/features/contactUs/types";

export function toContactUsDisplayProps(data: ContactUsPageInitialData["contactUsPage"]): ContactUsDisplayProps {
  return {
    subjects: data.subjects,
    ...(data.selectedSubject !== undefined ? { selectedSubject: data.selectedSubject } : {}),
    ...(data.message !== undefined ? { message: data.message } : {}),
    titles: data.titles.map((title) => ({
      label: title.label ?? "",
      value: title.value,
    })),
    salutations: data.salutations.map((salutation) => ({
      label: salutation.label ?? "",
      value: salutation.value,
    })),
    user: {
      salutation: data.user.salutation ?? "",
      title: data.user.title ?? "",
      firstName: data.user.firstName ?? "",
      lastName: data.user.lastName ?? "",
      phone: data.user.phone ?? "",
      email: data.user.email ?? "",
    },
    salesRepresentative: {
      fullAddress: data.salesRepresentative.fullAddress ?? "",
    },
  };
}

function ContactUsRoute() {
  const result = useLoaderData({
    from: "/contact-us",
  }) as PageInitialData<ContactUsPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const contactUsPage = result.data?.contactUsPage;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {contactUsPage ? createContactUsPage(toContactUsDisplayProps(contactUsPage)) : null}
    </StorefrontShellFrame>
  );
}

export async function loadContactUsPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(contactUsPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const contactUsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contact-us",
  loader: async ({ context }) => loadContactUsPageData(context),
  component: ContactUsRoute,
});
